import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QuestionService, ListOfQuestions } from "../../../../swaggerServices";
import { IFormField } from "../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DropdownListOption } from "../../../shared/components/search-dropdown/search-dropdown.component";
import { Subscription, Observable } from "rxjs";
import { TaxYearsService } from "../../../shared/util/tax-years.service";
import { ActivatedRoute } from "@angular/router";
import { ConfigStateService } from "../../config/config-state.service";
import { Tenant } from "../../../shared/types/tenant.enum";
import { TaxReturnTypeSubTypeService } from '../../../shared/util/tax-return-type-subtype.service';
import { debounceTime } from 'rxjs/operators';
import moment from 'moment';
import { ClientNotificationService } from '../../client-notification.service';
import { environment } from '../../../environments/environment';
import { TenantConfigService } from '../../../shared/services/tenant-config.service';
import { TranslocoService} from "@jsverse/transloco";
import { disableButton, enableButton } from '../../../shared/util/toggleButton';

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY HH:mm";

declare var $: any;

interface IConfigExportQuestion extends IFormField {
  selected: boolean;
  displayUpdatedAt?: string;
  displayCreatedAt?: string;
  name: string;
  calcUpdatedAt: string;
}

interface IquestionObject {
  id?: number;
  question?: string;
}

interface IdeleteQuestions {
  id?: number;
  question?: string;
  dependentQuestion?: IquestionObject[];
}

interface IdeleteCategories {
  id?: number;
  name?: string;
  order?: number;
}

interface IdeleteSubcategories {
  id?: number;
  name?: string;
  order?: number;
}

interface IdeleteSubgroups {
  id?: number;
  name?: string;
}

interface IdeleteQuestionDropdowns {
  id?: number;
  value?: string;
  display?: string;
}

@Component({
    selector: 'app-questions',
    templateUrl: './questions.component.html',
    styleUrls: ['./questions.component.scss'],
    standalone: false
})
export class QuestionsComponent implements OnInit, OnDestroy {
  loading = false;
  readonly pageSizeOptions = [50, 100, 200];
  questionPage = 1;
  configPage = 1;
  questionPageSize = 100;
  configPageSize = 100;
  questions: IFormField[];
  @ViewChild('dropDown') dropDown: ElementRef;
  questionTypeFilter: any;
  questionSearch: string;
  @ViewChild('multiSelectDropdown') multiSelectDropdown: ElementRef;
  @ViewChild('configExportConfimationModal') configExportConfimationModal: ElementRef;
  @ViewChild('configDeleteCheckModal') configDeleteCheckModal: ElementRef;
  @ViewChild('configDeleteConfimationModal') configDeleteConfimationModal: ElementRef;
  questionsByYearAndType: {} = {};
  searchForm: UntypedFormGroup = new UntypedFormGroup({});
  taxYear: UntypedFormControl;
  taxYearOptions: DropdownListOption[];
  taxReturnType: UntypedFormControl;
  taxReturnTypeOptions: DropdownListOption[];
  subscriptions: Subscription[] = [];
  showQuestionId: boolean;
  configOperations = {
    export: "export",
    delete: "delete"
  }
  configOperation: string = this.configOperations.export;
  configOperationDate = "";
  configDeletePossible:boolean = false; 
  configExportMode = false;
  configDeleteMode: boolean = false;
  entireListSelected = false;
  deleteQuestions: IdeleteQuestions[] = [];
  deleteCategories: IdeleteCategories[] = [];
  deleteSubcategories: IdeleteSubcategories[] = [];
  deleteSubgroups: IdeleteSubgroups[] = [];
  deleteQuestionDropdowns: IdeleteQuestionDropdowns[] = [];
  questionsForConfigExport: IConfigExportQuestion[];
  configExportDate = "";
  selectedQuestions = [];
  // exact match for yyyy-mm-dd
  dateValidationRegex = new RegExp("^\\d{4}\\-(0[1-9]|1[012])\\-(0[1-9]|[12][0-9]|3[01])$");
  isProductionMode: boolean;
  uiConfig: TenantConfigService;
  yearLabel: string;
  configYear: number;
  creatingBackup: boolean = false;
  
  constructor(private questionService: QuestionService,
    private cd: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private taxYearsService: TaxYearsService,
    private taxReturnTypeService: TaxReturnTypeSubTypeService,
    private clientNotificationService: ClientNotificationService,
    public config: ConfigStateService,
    private translocoService: TranslocoService) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {

    setTimeout(() =>
      $('.multi-select.dropdown').dropdown({
        placeholder: "All",
        preserveHTML: false,
      }));

    this.config.loadConfig().subscribe(() => {
      this.showQuestionId = this.config.tenant !== Tenant.AU;
    });
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService.selectTranslate('year').subscribe(value => this.yearLabel = value);
      })
    );
    this.taxYear = new UntypedFormControl('');
    this.taxReturnType = new UntypedFormControl('');
    this.searchForm = new UntypedFormGroup({
      taxYear: this.taxYear,
      taxReturnType: this.taxReturnType,
    });

    this.subscriptions.push(this.taxYearsService.getTaxYears().subscribe((years: any) => {
      this.taxYearOptions = years;
      const taxYr = this.activatedRoute.snapshot.queryParams.taxYear || this.taxYearsService.getCurrentTaxYear();
      this.configYear = taxYr;
      this.taxYear.setValue(taxYr);
    }));

    this.subscriptions.push(this.taxReturnTypeService.getTaxReturnTypes(true).subscribe((options: any) => {
      this.taxReturnTypeOptions = options;
      const [firstOption] = options;
      this.taxReturnType.setValue(firstOption.value);
    }));

    this.subscriptions.push(this.taxYear.valueChanges
      .subscribe(() => {
        this.getQuestionsByYearAndTaxReturnType(this.taxYear.value.value, this.taxReturnType.value.value);
      }));

    this.subscriptions.push(this.taxReturnType.valueChanges
      .pipe(debounceTime(400))
      .subscribe(() => {
        this.getQuestionsByYearAndTaxReturnType(this.taxYear.value.value, this.taxReturnType.value.value)
      }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getQuestionsByYearAndTaxReturnType(year: number, selectedType: string) {
    if (!year) return false;
    this.questionPage = 1;

    const type = !selectedType ? 0 : Number.parseInt(selectedType, 10);
    const key = `${year}-${type}`;
    if (this.questionsByYearAndType[key]) {
      this.questions = this.questionsByYearAndType[key];
      return this.loadQuestionsForConfigOperation();
    }
    this.loading = true;
    this.cd.detectChanges();

    var questionsObs: Observable<ListOfQuestions>;
    if (type) {
      questionsObs = this.questionService.getQuestionsByyear(year, type);
    } else {
      questionsObs = this.questionService.getQuestionsByyear(year);
    }

    questionsObs.subscribe((r: ListOfQuestions) => {
      this.questions = r.questions.map(q => {
        return {
          ...q,
          displayTag: q.tag && q.tag.length ? `tag={${q.tag}}` : '',
          questionType: this.getQuestionType(q)
        };
      });
      this.questionsByYearAndType[key] = this.questions;
      this.loadQuestionsForConfigOperation();
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  resetConfigFilters(){
    setTimeout(() => {
      $('.multi-select.dropdown').dropdown({
        placeholder: "All",
        preserveHTML: false,
      });
    });
  }

  toggleConfigOperationMode(operation){
    this.configOperation = operation;
    this.configOperationDate = "";
    this.filterQuestionsOnDateChange();
    if(operation === this.configOperations.export){
      this.configDeleteMode = false;
      this.configExportMode = !this.configExportMode;
    } else if(operation === this.configOperations.delete){
      this.configExportMode = false;
      this.configDeleteMode = !this.configDeleteMode;
    } else {
      this.configExportMode = false;
      this.configDeleteMode = false;
    }
    if(!this.configExportMode && !this.configDeleteMode){
      this.resetConfigFilters();
    }
  }

  entireListSelection() {
    this.questionsForConfigExport.forEach((q) => q.selected = this.entireListSelected);    
  }

  listSelection() {    
    this.entireListSelected = !!this.questionsForConfigExport.length
      && this.questionsForConfigExport.every((q) => q.selected);
  }  

  onQuestionFiltersChanged() {
    this.questionPage = 1;
  }

  onQuestionPageSizeChanged() {
    this.questionPage = 1;
  }

  onConfigPageSizeChanged() {
    this.configPage = 1;
  }

  getTotalPages(total: number, pageSize: number): number {
    return Math.max(1, Math.ceil(total / pageSize));
  }

  getPaginationItems(currentPage: number, totalItems: number, pageSize: number): Array<number | string> {
    const totalPages = this.getTotalPages(totalItems, pageSize);
    if (totalPages <= 1) {
      return [1];
    }

    const current = Math.min(Math.max(currentPage, 1), totalPages);
    const start = Math.max(2, current - 3);
    const end = Math.min(totalPages - 1, current + 3);
    const items: Array<number | string> = [1];

    if (start > 2) {
      items.push('...');
    }
    for (let page = start; page <= end; page++) {
      items.push(page);
    }
    if (end < totalPages - 1) {
      items.push('...');
    }
    items.push(totalPages);
    return items;
  }

  setQuestionPage(page: number, total: number) {
    this.questionPage = this.clampPage(page, total, this.questionPageSize);
  }

  setConfigPage(page: number, total: number) {
    this.configPage = this.clampPage(page, total, this.configPageSize);
  }

  getSelectedConfigQuestionsCount(): number {
    return this.questionsForConfigExport?.filter(q => q.selected).length || 0;
  }

  private clampPage(page: number, total: number, pageSize: number): number {
    return Math.min(Math.max(page, 1), this.getTotalPages(total, pageSize));
  }

  loadQuestionsForConfigOperation(ids?: string[]) {
    this.configPage = 1;
    this.entireListSelected = false;
    this.questionsForConfigExport = this.questions.map((q: any) => {
      if (ids && !ids.includes(q.id)) {
        return;
      }
      return {
        selected: false,
        displayCreatedAt: moment(q.createdAt).format(DISPLAY_DATE_FORMAT).toString(),
        displayUpdatedAt: moment(q.updatedAt).format(DISPLAY_DATE_FORMAT).toString(),
        name: q["calculationPlans.user.firstName"] ? `${q["calculationPlans.user.firstName"]} ${q["calculationPlans.user.lastName"]}` : "n/a",
        calcUpdatedAt: q["calculationPlans.updatedAt"] ? moment(q["calculationPlans.updatedAt"]).format(DISPLAY_DATE_FORMAT).toString() : "n/a",
        ...q
      }
    }).filter(q => q);
  }

  filterQuestionsOnDateChange() {
    if (!this.configOperationDate.length) {
      this.loadQuestionsForConfigOperation();
      return;
    }
    if (this.dateValidationRegex.test(this.configOperationDate)) {
      this.loading = true;
      this.questionService.getUpdatedQuestionsByDate(this.configOperationDate).subscribe((ids) => {
        this.loading = false;
        this.loadQuestionsForConfigOperation(ids);
      });
    }
  }

  openConfigOperationConfimationModal(operation) {
    this.selectedQuestions = this.questionsForConfigExport.map((q) => {
      if (q.selected) { return { id: q.id, question: q.question, displayUpdatedAt: q.displayUpdatedAt } }
    }).filter(tt => tt);
    if(operation === this.configOperations.export){
      $(this.configExportConfimationModal.nativeElement).modal("show");
      $(this.configExportConfimationModal.nativeElement).scrollTop($(this.configExportConfimationModal.nativeElement)[0].scrollHeight);
    } else if (operation === this.configOperations.delete){
      $(this.configDeleteCheckModal.nativeElement).modal("show");
      $(this.configDeleteCheckModal.nativeElement).scrollTop($(this.configDeleteCheckModal.nativeElement)[0].scrollHeight);
    }
  }

  clearSelectedSources() {
    this.entireListSelected = false;
    this.questionsForConfigExport.forEach((q) => q.selected = false);
  }

  clearDeleteConfig() {
    $(this.configDeleteCheckModal.nativeElement).modal("hide");
    $(this.configDeleteConfimationModal.nativeElement).modal("hide");
  }

  downloadConfig() {
    if (!this.selectedQuestions.length) { return; };
    const questionIds = this.selectedQuestions.map(q => q.id);
    this.questionService.downloadConfigExportQuestion({ questionIds }).subscribe(data => {
      if (!data.fileContent) {
        this.clientNotificationService.appendMessage("File couldn't be fetched, please try again later", "Download config", false);
        return;
      }
      var elem = window.document.createElement('a');
      elem.href = "data:application/json," + encodeURIComponent(data.fileContent);
      elem.download = data.fileName ? data.fileName : "package";
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
      this.clearSelectedSources();
      $(this.configExportConfimationModal.nativeElement).modal("hide");
    }, error => {
      $(this.configExportConfimationModal.nativeElement).modal("hide");

    })
  }

  createBackup() {
    this.creatingBackup = true;
    this.questionService.backupQuestionConfig({year:this.configYear}).subscribe(()=>{
      this.creatingBackup = false;
    }, error => {
      this.creatingBackup = false;
    });
  }

  deleteConfig({confirmed, event}) {
    if (!this.selectedQuestions.length) { return; };
    const questionIds: number[] = this.selectedQuestions.map(q => q.id);
    disableButton(event.target);
    this.questionService.deleteQuestionConfig({ questionIds, confirmed }).subscribe(data => {
      enableButton(event.target);
      if(data.deleteConfig && confirmed && data.deleteConfig.deleted ){
        this.clearSelectedSources();
        this.clearDeleteConfig();
        window.location.reload();
      } else if(data.deleteConfig && !confirmed){
        const dialogDismissed = !$(this.configDeleteCheckModal.nativeElement).modal("is active");
        if (dialogDismissed) { return; }
        $(this.configDeleteCheckModal.nativeElement).modal("hide");
        this.configDeletePossible = data.deletePossible;
        this.deleteQuestions = data.deleteConfig?.questions;
        this.deleteSubgroups = data.deleteConfig.subgroups ? data.deleteConfig.subgroups : [];
        this.deleteSubcategories = data.deleteConfig.subcategories ? data.deleteConfig.subcategories : [];
        this.deleteCategories = data.deleteConfig?.categories ? data.deleteConfig?.categories : [];
        $(this.configDeleteConfimationModal.nativeElement).modal("show");
      } else {
        this.clearSelectedSources();
        this.clearDeleteConfig();
      }
    }, error => {
      enableButton(event.target);
      this.clearSelectedSources();
      this.clearDeleteConfig();
    })
  }

  getQuestionType(q: IFormField): string {
    let questionType = q.displayFieldType;
    if (q.displayFieldType === 'text') {
      if (q.dataType === 'string') {
        questionType = 'text';
      } else if (q.dataType.toLowerCase() === 'integer' || q.dataType.toLowerCase() === 'float') {
        questionType = 'number';
      }
    }
    if (q.dataType === 'table') {
      questionType = 'table';
    }
    return questionType;
  };

}
